// Stream/chat lifecycle support extracted from useAgentStore.js. After the
// sequence-gated lifecycle reducer became the single owner of chatStatus
// (chatLifecycleReducer.js), this module holds only the two pieces that are NOT
// chat-lifecycle arbitration:
//   1. the 750ms session-*list* metadata refetch debounce, and
//   2. the chatStatus-transition reason channel (a diagnostic breadcrumb).
// The submitted/cancel safety timers, the post-complete REST guard map, the
// stuck-spinner tool-lifecycle map, and the unresolved-stream audit were all
// band-aids for the client mis-transition race; the reducer's invariant
// (seqId/turnEpoch/sseAuthority/terminalApplied gating) eliminates that race, so
// they were deleted rather than carried forward.
//
// These intentionally take the store's `getStore` accessor as a parameter rather
// than importing useAgentStore, so this module has NO dependency on the store and
// cannot create an import cycle.
//
// SINGLE-STORE CONTRACT: the timer/registry below are module-level singletons
// shared by the one and only `useAgentStore` instance. This app never creates a
// second agent store, so a single instance is correct and intentional (not a
// missed dependency injection). The state is not hidden from tests: call
// `_resetStreamLifecycle()` (e.g. in a test `beforeEach`) to cancel the pending
// timer and run any registered aux resets back to a clean baseline.

import { isSessionRowMetadataPending } from "./agentSessionsPanelUtils";

let _sessionListRefetchTimer = null;
let _sessionListRefetchAgentId = null;

export function _schedulePendingSessionListRefetch(agentId, getStore) {
  const normalizedAgentId = String(agentId || "").trim();
  if (!normalizedAgentId) return;
  _sessionListRefetchAgentId = normalizedAgentId;
  if (_sessionListRefetchTimer) {
    clearTimeout(_sessionListRefetchTimer);
  }
  _sessionListRefetchTimer = setTimeout(() => {
    _sessionListRefetchTimer = null;
    const pendingAgentId = _sessionListRefetchAgentId;
    _sessionListRefetchAgentId = null;
    if (!pendingAgentId) return;
    const sessions = getStore().sessions;
    if (!sessions.some(isSessionRowMetadataPending)) return;
    getStore().fetchSessions(pendingAgentId, { silent: true });
  }, 750);
}

// chatStatus-transition reason channel. The reason recorded by the action that
// drives a transition is written into store state via the injected sink, so it
// travels WITH the store (visible in devtools, resettable in tests) instead of
// living in a module global. useAgentStore wires the sink once during init and
// the transition-logging subscription reads state.chatStatusReason directly.
// The reducer wrapper calls _markChatStatusReason before mirroring chatStatus,
// so the reason is always fresh by the time the chatStatus set fires.
let _chatStatusReasonSink = null;
export function _setChatStatusReasonSink(sink) {
  _chatStatusReasonSink = sink;
}
export function _markChatStatusReason(reason) {
  if (_chatStatusReasonSink) _chatStatusReasonSink(reason);
}

// Other store modules can register a reset callback here so that
// `_resetStreamLifecycle` stays the single teardown entry point tests call,
// without this module importing those slices (which would create a cycle —
// they import from here).
const _auxResetCallbacks = new Set();
export function _registerLifecycleReset(resetFn) {
  _auxResetCallbacks.add(resetFn);
}

// Cancels the pending session-list refetch timer and runs any registered aux
// resets back to a clean baseline. The reason sink is left wired (it is keyed to
// the store, not per-test). Exposed so tests can reset the module-level
// singletons between cases and so teardown paths can stop dangling timers.
export function _resetStreamLifecycle() {
  if (_sessionListRefetchTimer) {
    clearTimeout(_sessionListRefetchTimer);
    _sessionListRefetchTimer = null;
  }
  _sessionListRefetchAgentId = null;
  for (const reset of _auxResetCallbacks) reset();
}
